import {
  applyPath,
  boardRectToScreen,
  boardToScreen,
  connectorGeometry,
  nodeAABB,
  shapePath,
  type BoardNode,
  type NodeLookup,
  type Presence,
  type Rect,
  type ShapeKind,
  type SnapGuide,
  type Vec,
  type Viewport,
} from '@kanvas/board-core';
import { LASER_FADE_MS, type ConnectorPreview, type LaserTrail } from '../state/scratch';
import { HANDLE_SIZE, frameScreenCorners, handleSpecs, type SelectionFrame } from './handles';

/**
 * Selection chrome, kept in step with `--accent` in styles.css by hand: the
 * canvas is painted rather than styled, so it cannot read the token. If the
 * two drift, selection outlines stop matching the toolbar that produced them.
 */
export const ACCENT = '#4F46E5';
const ACCENT_SOFT = 'rgba(79, 70, 229, 0.14)';
const GUIDE = '#F0435F';

/**
 * All overlay painting happens in screen space with the camera transform
 * reset. Selection chrome must not scale with zoom: a 1px outline stays 1px,
 * and handles stay the same physical size whether the user is at 5% or 800%.
 */

export function paintSelectionOutline(
  ctx: CanvasRenderingContext2D,
  frame: SelectionFrame,
  vp: Viewport,
  options: { dashed?: boolean; color?: string; width?: number } = {},
): void {
  const corners = frameScreenCorners(frame, vp);
  ctx.save();
  ctx.strokeStyle = options.color ?? ACCENT;
  ctx.lineWidth = options.width ?? 1.5;
  if (options.dashed) ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(corners[0]!.x, corners[0]!.y);
  for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

export function paintHandles(
  ctx: CanvasRenderingContext2D,
  frame: SelectionFrame,
  vp: Viewport,
  opts: { resize?: boolean } = {},
): void {
  const specs = handleSpecs(frame, vp, opts);
  if (specs.length === 0) return;

  ctx.save();
  for (const spec of specs) {
    if (spec.id === 'rotate') {
      ctx.beginPath();
      ctx.arc(spec.point.x, spec.point.y, HANDLE_SIZE / 2 + 0.5, 0, Math.PI * 2);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      continue;
    }
    const s = HANDLE_SIZE;
    ctx.beginPath();
    ctx.rect(spec.point.x - s / 2, spec.point.y - s / 2, s, s);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Dim mask outside the crop window plus handles, while an image is being cropped.
 * `source` is the full-image board rect; `crop` is 0..1 inside that source.
 * When `mask` is set, the clear hole follows the freehand polygon instead of the box.
 * `livePath` is the in-progress lasso in board coordinates.
 */
export function paintCropOverlay(
  ctx: CanvasRenderingContext2D,
  source: Rect,
  crop: Rect,
  vp: Viewport,
  options: {
    mask?: Vec[] | null;
    livePath?: Vec[] | null;
    /** Image rotation, so the window sits on the photo rather than across it. */
    rotation?: { center: Vec; angle: number } | null;
  } = {},
): void {
  const src = boardRectToScreen(vp, source);
  const cropBoard: Rect = {
    x: source.x + crop.x * source.w,
    y: source.y + crop.y * source.h,
    w: crop.w * source.w,
    h: crop.h * source.h,
  };
  const win = boardRectToScreen(vp, cropBoard);
  const mask = options.mask;
  const livePath = options.livePath;

  ctx.save();
  if (options.rotation && options.rotation.angle !== 0) {
    const c = boardToScreen(vp, options.rotation.center);
    ctx.translate(c.x, c.y);
    ctx.rotate(options.rotation.angle);
    ctx.translate(-c.x, -c.y);
  }
  ctx.fillStyle = 'rgba(15, 18, 25, 0.45)';
  ctx.beginPath();
  ctx.rect(src.x, src.y, src.w, src.h);
  if (mask && mask.length >= 3) {
    for (let i = 0; i < mask.length; i++) {
      const p = mask[i]!;
      const sp = boardToScreen(vp, {
        x: source.x + p.x * source.w,
        y: source.y + p.y * source.h,
      });
      if (i === 0) ctx.moveTo(sp.x, sp.y);
      else ctx.lineTo(sp.x, sp.y);
    }
    ctx.closePath();
  } else {
    ctx.rect(win.x, win.y, win.w, win.h);
  }
  ctx.fill('evenodd');

  if (mask && mask.length >= 3) {
    ctx.beginPath();
    for (let i = 0; i < mask.length; i++) {
      const p = mask[i]!;
      const sp = boardToScreen(vp, {
        x: source.x + p.x * source.w,
        y: source.y + p.y * source.h,
      });
      if (i === 0) ctx.moveTo(sp.x, sp.y);
      else ctx.lineTo(sp.x, sp.y);
    }
    ctx.closePath();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.25;
    ctx.setLineDash([5, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  } else {
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(win.x, win.y, win.w, win.h);
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(win.x, win.y, win.w, win.h);
    ctx.setLineDash([]);
  }

  if (livePath && livePath.length >= 2) {
    ctx.beginPath();
    const first = boardToScreen(vp, livePath[0]!);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < livePath.length; i++) {
      const p = boardToScreen(vp, livePath[i]!);
      ctx.lineTo(p.x, p.y);
    }
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([]);
    ctx.stroke();
  }

  // Rectangular handles stay available so the AABB can still be tweaked.
  const handles: Vec[] = [
    { x: win.x, y: win.y },
    { x: win.x + win.w / 2, y: win.y },
    { x: win.x + win.w, y: win.y },
    { x: win.x + win.w, y: win.y + win.h / 2 },
    { x: win.x + win.w, y: win.y + win.h },
    { x: win.x + win.w / 2, y: win.y + win.h },
    { x: win.x, y: win.y + win.h },
    { x: win.x, y: win.y + win.h / 2 },
  ];
  const s = HANDLE_SIZE;
  for (const p of handles) {
    ctx.beginPath();
    ctx.rect(p.x - s / 2, p.y - s / 2, s, s);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
  ctx.restore();
}

/** Thin outline drawn around every member of a multi-selection. */
export function paintMemberOutlines(
  ctx: CanvasRenderingContext2D,
  nodes: readonly BoardNode[],
  vp: Viewport,
  lookup: NodeLookup,
): void {
  if (nodes.length < 2) return;
  ctx.save();
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.55)';
  ctx.lineWidth = 1;
  for (const node of nodes) {
    const r = boardRectToScreen(vp, nodeAABB(node, lookup));
    ctx.strokeRect(r.x, r.y, r.w, r.h);
  }
  ctx.restore();
}

export function paintHover(
  ctx: CanvasRenderingContext2D,
  node: BoardNode,
  vp: Viewport,
  lookup: NodeLookup,
): void {
  ctx.save();
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.75)';
  ctx.lineWidth = 1.5;
  if (node.type === 'connector') {
    const geo = connectorGeometry(node, lookup);
    ctx.beginPath();
    const first = boardToScreen(vp, geo.points[0]!);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < geo.points.length; i++) {
      const p = boardToScreen(vp, geo.points[i]!);
      ctx.lineTo(p.x, p.y);
    }
    ctx.lineWidth = Math.max(node.strokeWidth * vp.zoom + 4, 6);
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.28)';
    ctx.stroke();
    ctx.restore();
    return;
  }
  const r = boardRectToScreen(vp, nodeAABB(node, lookup));
  ctx.strokeRect(r.x, r.y, r.w, r.h);
  ctx.restore();
}

export function paintHighlight(
  ctx: CanvasRenderingContext2D,
  node: BoardNode,
  vp: Viewport,
  lookup: NodeLookup,
): void {
  const r = boardRectToScreen(vp, nodeAABB(node, lookup));
  ctx.save();
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 2.5;
  ctx.setLineDash([]);
  ctx.strokeRect(r.x - 2, r.y - 2, r.w + 4, r.h + 4);
  ctx.restore();
}

export function paintMarquee(ctx: CanvasRenderingContext2D, board: Rect, vp: Viewport): void {
  const r = boardRectToScreen(vp, board);
  ctx.save();
  ctx.fillStyle = ACCENT_SOFT;
  ctx.fillRect(r.x, r.y, r.w, r.h);
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 1;
  ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w, r.h);
  ctx.restore();
}

export function paintGuides(
  ctx: CanvasRenderingContext2D,
  guides: readonly SnapGuide[],
  vp: Viewport,
): void {
  if (guides.length === 0) return;
  ctx.save();
  ctx.strokeStyle = GUIDE;
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 4]);

  for (const g of guides) {
    if (g.axis === 'x') {
      const x = Math.round((g.at - vp.x) * vp.zoom) + 0.5;
      const from = (g.from - vp.y) * vp.zoom;
      const to = (g.to - vp.y) * vp.zoom;
      ctx.beginPath();
      ctx.moveTo(x, from);
      ctx.lineTo(x, to);
      ctx.stroke();
    } else {
      const y = Math.round((g.at - vp.y) * vp.zoom) + 0.5;
      const from = (g.from - vp.x) * vp.zoom;
      const to = (g.to - vp.x) * vp.zoom;
      ctx.beginPath();
      ctx.moveTo(from, y);
      ctx.lineTo(to, y);
      ctx.stroke();
    }

    if (g.gap !== undefined) {
      // Equal-spacing guides get a badge with the measured gap.
      const label = `${Math.round(g.gap)}`;
      const mid =
        g.axis === 'x'
          ? { x: (g.at - vp.x) * vp.zoom, y: ((g.from + g.to) / 2 - vp.y) * vp.zoom }
          : { x: ((g.from + g.to) / 2 - vp.x) * vp.zoom, y: (g.at - vp.y) * vp.zoom };
      ctx.setLineDash([]);
      ctx.font = '600 10px Inter, system-ui, sans-serif';
      const w = ctx.measureText(label).width + 8;
      ctx.fillStyle = GUIDE;
      ctx.beginPath();
      ctx.roundRect?.(mid.x - w / 2, mid.y - 8, w, 16, 8);
      ctx.fill();
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, mid.x, mid.y);
      ctx.setLineDash([5, 4]);
    }
  }
  ctx.restore();
}

export function paintCreationPreview(
  ctx: CanvasRenderingContext2D,
  preview: Rect & { kind: string },
  vp: Viewport,
  shapeKind: ShapeKind,
): void {
  const r = boardRectToScreen(vp, preview);
  ctx.save();
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([5, 4]);
  if (preview.kind === 'shape') {
    ctx.beginPath();
    applyPath(shapePath(shapeKind, r, 8), ctx);
    ctx.stroke();
  } else {
    ctx.strokeRect(r.x, r.y, r.w, r.h);
  }
  ctx.restore();
}

export function paintConnectorPreview(
  ctx: CanvasRenderingContext2D,
  preview: ConnectorPreview,
  vp: Viewport,
): void {
  const a = boardToScreen(vp, preview.from);
  const b = boardToScreen(vp, preview.to);
  ctx.save();
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 2;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  if (preview.routing === 'elbow') {
    const mx = (a.x + b.x) / 2;
    ctx.lineTo(mx, a.y);
    ctx.lineTo(mx, b.y);
  } else if (preview.routing === 'curved') {
    const pull = Math.abs(b.x - a.x) * 0.5;
    ctx.bezierCurveTo(a.x + pull, a.y, b.x - pull, b.y, b.x, b.y);
  }
  ctx.lineTo(b.x, b.y);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(b.x, b.y, 4, 0, Math.PI * 2);
  ctx.fillStyle = ACCENT;
  ctx.fill();
  ctx.restore();
}

/** The four connector anchors shown when hovering a node with the arrow tool. */
export function paintAnchors(
  ctx: CanvasRenderingContext2D,
  node: BoardNode,
  vp: Viewport,
  hovered: Vec | null,
): void {
  const anchors: Vec[] = [
    { x: node.x + node.w / 2, y: node.y },
    { x: node.x + node.w, y: node.y + node.h / 2 },
    { x: node.x + node.w / 2, y: node.y + node.h },
    { x: node.x, y: node.y + node.h / 2 },
  ];
  ctx.save();
  for (const a of anchors) {
    const p = boardToScreen(vp, a);
    const active = hovered && Math.hypot(hovered.x - a.x, hovered.y - a.y) < 1e-6;
    ctx.beginPath();
    ctx.arc(p.x, p.y, active ? 6 : 4.5, 0, Math.PI * 2);
    ctx.fillStyle = active ? ACCENT : '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
  ctx.restore();
}

export function paintEraser(
  ctx: CanvasRenderingContext2D,
  point: Vec,
  radiusBoard: number,
  vp: Viewport,
): void {
  const p = boardToScreen(vp, point);
  ctx.save();
  ctx.strokeStyle = 'rgba(20,25,35,0.55)';
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(p.x, p.y, radiusBoard * vp.zoom, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/**
 * On-canvas brush tip so pen / marker / highlighter each read as themselves.
 *
 * Built the same way as the toolbar glyphs (see `icons.tsx`): the tool lies
 * on one axis with its tip on the hot-spot, rotated up and to the right so
 * the hand holding it would be off the bottom-right of the screen. The tip is
 * painted in the ink colour and grows with the brush width, so what is under
 * the pointer is the mark about to be made; the body is a fixed-size dark
 * handle that stays legible over any ink.
 */
export function paintBrushCursor(
  ctx: CanvasRenderingContext2D,
  point: Vec,
  brush: 'pen' | 'marker' | 'highlighter',
  color: string,
  size: number,
  vp: Viewport,
): void {
  const p = boardToScreen(vp, point);
  const inkPx = size * vp.zoom;
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(-Math.PI / 4);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  const BODY = 'rgba(32, 36, 48, 0.94)';
  const EDGE = 'rgba(255, 255, 255, 0.95)';
  const SHEEN = 'rgba(255, 255, 255, 0.28)';

  if (brush === 'pen') {
    // Fountain pen: nib with a slit and breather hole, collar, slim barrel.
    const nibW = Math.max(3.4, Math.min(inkPx * 0.9, 6));
    const nibL = nibW * 3.2;
    const barrelW = nibW * 1.25;
    const barrelL = 22;

    // Barrel first so the nib overlaps its collar.
    roundRectPathLocal(ctx, nibL + 1, -barrelW, barrelL, barrelW * 2, barrelW);
    ctx.fillStyle = BODY;
    ctx.fill();
    ctx.strokeStyle = EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();
    // Sheen along the upper edge of the barrel.
    ctx.beginPath();
    ctx.moveTo(nibL + 6, -barrelW * 0.45);
    ctx.lineTo(nibL + barrelL - 5, -barrelW * 0.45);
    ctx.strokeStyle = SHEEN;
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Nib in the ink colour.
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(nibL * 0.55, -nibW);
    ctx.lineTo(nibL + 1.5, -nibW);
    ctx.lineTo(nibL + 1.5, nibW);
    ctx.lineTo(nibL * 0.55, nibW);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();
    // Slit and breather hole.
    ctx.beginPath();
    ctx.moveTo(1.2, 0);
    ctx.lineTo(nibL * 0.5, 0);
    ctx.strokeStyle = 'rgba(20, 25, 35, 0.5)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(nibL * 0.56, 0, Math.max(0.9, nibW * 0.22), 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(20, 25, 35, 0.5)';
    ctx.fill();
    // Glint on the very tip.
    ctx.beginPath();
    ctx.arc(2.2, -0.9, 0.8, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fill();
  } else if (brush === 'marker') {
    // Felt marker: slanted chisel, collar, wide barrel with a cap ring.
    const w = Math.max(4, Math.min(inkPx * 0.55, 9));
    const chiselL = w * 1.8;
    const barrelL = 26;

    roundRectPathLocal(ctx, chiselL, -w, barrelL, w * 2, 2.5);
    ctx.fillStyle = BODY;
    ctx.fill();
    ctx.strokeStyle = EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();
    // Cap ring a little way up the barrel, in the ink colour.
    ctx.fillStyle = color;
    ctx.fillRect(chiselL + 5, -w, 3, w * 2);
    ctx.beginPath();
    ctx.moveTo(chiselL + 6, -w * 0.45);
    ctx.lineTo(chiselL + barrelL - 4, -w * 0.45);
    ctx.strokeStyle = SHEEN;
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Chisel: the front edge is cut on a slant, which is what makes a marker
    // stroke thick one way and thin the other.
    ctx.beginPath();
    ctx.moveTo(0, -w * 0.35);
    ctx.lineTo(w * 0.7, w * 0.55);
    ctx.lineTo(chiselL + 1, w);
    ctx.lineTo(chiselL + 1, -w);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();
  } else {
    // Highlighter: flat tip as wide as the body, short stubby handle.
    const w = Math.max(6, Math.min(inkPx * 0.5, 14));
    const tipL = w * 0.9;
    const bodyL = 22;

    roundRectPathLocal(ctx, tipL, -w * 1.1, bodyL, w * 2.2, 3);
    ctx.fillStyle = BODY;
    ctx.fill();
    ctx.strokeStyle = EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.85;
    ctx.fillRect(tipL + 4, -w * 1.1, 3.5, w * 2.2);
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.moveTo(tipL + 9, -w * 0.5);
    ctx.lineTo(tipL + bodyL - 4, -w * 0.5);
    ctx.strokeStyle = SHEEN;
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Translucent tip, like the ink it lays down.
    ctx.beginPath();
    ctx.moveTo(0, -w * 0.85);
    ctx.lineTo(0, w * 0.85);
    ctx.lineTo(tipL + 1, w * 1.1);
    ctx.lineTo(tipL + 1, -w * 1.1);
    ctx.closePath();
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = EDGE;
    ctx.lineWidth = 1.1;
    ctx.stroke();
  }

  ctx.restore();
}

function roundRectPathLocal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

export function paintLasers(
  ctx: CanvasRenderingContext2D,
  lasers: ReadonlyMap<string, LaserTrail>,
  vp: Viewport,
  now: number,
): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const trail of lasers.values()) {
    if (trail.points.length < 2) continue;
    const age = now - trail.updatedAt;
    const alpha = Math.max(0, 1 - age / LASER_FADE_MS);
    if (alpha <= 0) continue;

    // Draw the tail with a per-segment alpha ramp so it looks like it decays.
    const n = trail.points.length;
    for (let i = 1; i < n; i++) {
      const t = i / (n - 1);
      ctx.globalAlpha = alpha * t;
      ctx.strokeStyle = trail.color;
      ctx.lineWidth = 3 + 3 * t;
      const a = boardToScreen(vp, trail.points[i - 1]!);
      const b = boardToScreen(vp, trail.points[i]!);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Dashed rectangles showing where peers are looking, for the follow affordance. */
export function paintPresenceViewports(
  ctx: CanvasRenderingContext2D,
  peers: readonly Presence[],
  vp: Viewport,
  followingUserId: string | null,
): void {
  if (!followingUserId) return;
  const peer = peers.find((p) => p.userId === followingUserId);
  if (!peer?.viewport) return;
  const r = boardRectToScreen(vp, peer.viewport);
  ctx.save();
  ctx.strokeStyle = peer.color;
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 6]);
  ctx.strokeRect(r.x, r.y, r.w, r.h);
  ctx.restore();
}

/** Endpoint and bend grips on a selected connector. */
export function paintConnectorGrips(
  ctx: CanvasRenderingContext2D,
  node: BoardNode,
  vp: Viewport,
  lookup: NodeLookup,
): void {
  if (node.type !== 'connector') return;
  const geo = connectorGeometry(node, lookup);
  const grips = [geo.start, geo.end, geo.mid];

  ctx.save();
  grips.forEach((g, i) => {
    const p = boardToScreen(vp, g);
    ctx.beginPath();
    ctx.arc(p.x, p.y, i === 2 ? 4 : 5.5, 0, Math.PI * 2);
    ctx.fillStyle = i === 2 ? ACCENT : '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  });
  ctx.restore();
}
